import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../presets/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

/**
 * Cascades a default `size` to every descendant `<Button>` that does
 * NOT pass an explicit `size` prop. Wrap a region (e.g. a page header
 * rightAction slot) with `<ButtonSizeProvider size="header">` and
 * every plain `<Button>` inside inherits `size="header"` without a
 * per-call edit. Explicit props on descendants win — nothing to
 * migrate for existing sized buttons.
 */
type ButtonSize = 'default' | 'sm' | 'lg' | 'icon' | 'header';

const ButtonSizeContext = React.createContext<ButtonSize | undefined>(
  undefined
);

export const ButtonSizeProvider = ({
  size,
  children,
}: {
  size: ButtonSize;
  children: React.ReactNode;
}): React.ReactElement => (
  <ButtonSizeContext.Provider value={size}>
    {children}
  </ButtonSizeContext.Provider>
);

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        success: 'bg-green-500 text-white shadow-sm hover:bg-green-600',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        // `header` — single source of truth for the compact top-of-page
        // action buttons (rightAction slot on <Page />, list page
        // headers, detail view headers). Identical classes to `sm`
        // today, but semantic name lets shared components
        // (ExportActionsList, ScopeFilter) key layout on the same
        // token and lets the design token evolve without editing every
        // call site. Prefer `size="header"` over raw `size="sm"` at
        // top-of-page action sites; keep `sm` for form/table row
        // controls where compactness is unrelated to header layout.
        header: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  tooltip?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, tooltip, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    // Inherit a size from the nearest `ButtonSizeProvider` when the
    // caller didn't pass one. Explicit `size` always wins, so existing
    // sized buttons are untouched.
    const inheritedSize = React.useContext(ButtonSizeContext);
    const resolvedSize = size ?? inheritedSize ?? undefined;

    const button = (
      <Comp
        className={cn(
          buttonVariants({ variant, size: resolvedSize, className })
        )}
        ref={ref}
        {...props}
      />
    );

    if (!tooltip) return button;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Wrapper span ensures hover events fire even when button is disabled */}
            <span className="inline-flex w-full">{button}</span>
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
